import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { btnDark, btnLine, inputCls, labelCls } from "./shared";

function Field({ f, value, onChange, tid }) {
  if (f.type === "select") return (
    <select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
      <option value="">Select…</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>);
  if (f.type === "textarea") return <textarea rows={3} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  return <input type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
}

function Group({ f, value = {}, onChange, sec }) {
  return (
    <div className="border border-navy/15 p-4">
      <p className="mb-3 text-sm font-semibold text-navy">{f.label}{f.hint && <span className="ml-2 font-normal text-muted">{f.hint}</span>}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {f.options.map((s) => (
          <div key={s.key}><label className={labelCls}>{s.label}</label>
            <Field f={s} value={value[s.key]} onChange={(v) => onChange({ ...value, [s.key]: v })} tid={`f-${sec}-${f.key}-${s.key}`} /></div>
        ))}
      </div>
    </div>);
}

function ListEditor({ f, value = [], onChange, sec }) {
  const upd = (i, k, v) => onChange(value.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-navy">{f.label}{f.hint && <span className="ml-2 font-normal text-muted">{f.hint}</span>}</p>
      {value.map((row, i) => (
        <div key={i} className="mb-3 border border-navy/15 p-4" data-testid={`row-${sec}-${f.key}-${i}`}>
          <div className="grid gap-3 sm:grid-cols-2">
            {f.options.map((s) => (
              <div key={s.key}><label className={labelCls}>{s.label}</label>
                <Field f={s} value={row[s.key]} onChange={(v) => upd(i, s.key, v)} tid={`f-${sec}-${f.key}-${i}-${s.key}`} /></div>
            ))}
          </div>
          <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="mt-3 inline-flex items-center gap-1 text-xs text-coral"><Trash2 size={13} /> Remove</button>
        </div>
      ))}
      <button onClick={() => onChange([...value, {}])} className={btnLine} data-testid={`add-${sec}-${f.key}`}><Plus size={14} /> Add</button>
    </div>);
}

export default function Details({ caseId, schema, data, setData }) {
  const [active, setActive] = useState(schema[0].key);
  const [busy, setBusy] = useState(false);
  const sec = schema.find((s) => s.key === active);
  const vals = data[active] || {};
  const set = (k, v) => setData({ ...data, [active]: { ...vals, [k]: v } });
  const save = async () => {
    setBusy(true);
    try { await api.put(`/cases/${caseId}/prep/${active}`, { data: vals }); toast.success("Saved"); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="details-panel">
      <p className="mb-4 text-sm text-muted">Enter each fact once. Every official form, the CV and your letters are filled from these answers.</p>
      <div className="mb-6 flex flex-wrap gap-x-5 gap-y-2 border-b border-navy/10 pb-3">
        {schema.map((s) => (
          <button key={s.key} onClick={() => setActive(s.key)} data-testid={`sec-${s.key}`}
            className={`text-sm ${active === s.key ? "border-b-2 border-navy font-semibold text-navy" : "text-muted hover:text-navy"}`}>{s.title}</button>
        ))}
      </div>
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          {sec.fields.filter((f) => !["group", "list"].includes(f.type)).map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
              <label className={labelCls}>{f.label}</label>
              <Field f={f} value={vals[f.key]} onChange={(v) => set(f.key, v)} tid={`f-${active}-${f.key}`} />
              {f.hint && <p className="mt-1 text-xs text-muted">{f.hint}</p>}
            </div>
          ))}
        </div>
        {sec.fields.filter((f) => f.type === "group").map((f) => <Group key={f.key} f={f} value={vals[f.key]} onChange={(v) => set(f.key, v)} sec={active} />)}
        {sec.fields.filter((f) => f.type === "list").map((f) => <ListEditor key={f.key} f={f} value={vals[f.key]} onChange={(v) => set(f.key, v)} sec={active} />)}
      </div>
      <button onClick={save} disabled={busy} className={`${btnDark} mt-6`} data-testid="details-save">{busy ? "Saving…" : `Save ${sec.title.toLowerCase()}`}</button>
    </div>
  );
}
